import {
  BarChart3,
  ClipboardList,
  FilePlus2,
  Users,
  CalendarDays,
  Camera,
  CircleHelp,
  Clock,
  Coffee,
  KeyRound,
  ListChecks,
  LogIn,
  Route,
  Smartphone,
  type LucideIcon,
} from 'lucide-react'

export type GuideTopic = {
  slug: string
  title: string
  icon: LucideIcon
  /** Only ready topics are links; the rest render as a muted "Bald" card. */
  ready: boolean
}

export type GuidePhase = {
  title: string
  topics: GuideTopic[]
}

export const LOGIN_SLUG = 'anmelden'
export const TERMIN_ABLAUF_SLUG = 'termin-ablauf'
export const INSTALL_APP_SLUG = 'app-installieren'

export const GUIDE_ROLES = ['handwerker', 'buero', 'leiter'] as const
export type GuideRole = (typeof GUIDE_ROLES)[number]

export const GUIDE_ROLE_LABELS: Record<GuideRole, string> = {
  handwerker: 'Handwerker',
  buero: 'Büromitarbeiter',
  leiter: 'Leiter',
}

export const GUIDE_PHASES: Record<GuideRole, GuidePhase[]> = {
  handwerker: [
    {
      title: 'Erste Schritte',
      topics: [
        { slug: LOGIN_SLUG, title: 'Anmelden', icon: LogIn, ready: true },
        { slug: INSTALL_APP_SLUG, title: 'App installieren & Benachrichtigungen', icon: Smartphone, ready: true },
      ],
    },
    {
      title: 'Arbeitstag',
      topics: [
        { slug: 'kalender', title: 'Mein Kalender', icon: CalendarDays, ready: false },
        { slug: TERMIN_ABLAUF_SLUG, title: 'Termin-Ablauf', icon: Route, ready: true },
        { slug: 'aufgaben', title: 'Aufgaben erledigen', icon: ListChecks, ready: false },
        { slug: 'pausen', title: 'Pausen', icon: Coffee, ready: false },
      ],
    },
    {
      title: 'Rückmeldung & Zeiten',
      topics: [
        { slug: 'rueckmeldung', title: 'Kommentar und Fotos ans Büro', icon: Camera, ready: false },
        { slug: 'privatzeit', title: 'Privatzeit eintragen', icon: Clock, ready: false },
      ],
    },
    {
      title: 'Hilfe',
      topics: [
        { slug: 'passwort-vergessen', title: 'Passwort vergessen', icon: KeyRound, ready: false },
        { slug: 'probleme', title: 'Häufige Probleme', icon: CircleHelp, ready: false },
      ],
    },
  ],
  buero: [
    {
      title: 'Aufträge',
      topics: [
        { slug: 'auftrag-anlegen', title: 'Reparaturauftrag anlegen', icon: FilePlus2, ready: false },
        { slug: 'auftrag-verfolgen', title: 'Aufträge verfolgen', icon: ClipboardList, ready: false },
      ],
    },
    {
      title: 'Planung',
      topics: [
        { slug: 'disposition', title: 'Termine disponieren', icon: CalendarDays, ready: false },
        { slug: 'aufgaben-planen', title: 'Aufgaben planen', icon: ListChecks, ready: false },
        { slug: 'umdisponieren', title: 'Umdisponieren', icon: Route, ready: false },
      ],
    },
    {
      title: 'Auswertung',
      topics: [
        { slug: 'rueckmeldungen-lesen', title: 'Rückmeldungen der Handwerker', icon: Camera, ready: false },
        { slug: 'controlling', title: 'Controlling', icon: BarChart3, ready: false },
      ],
    },
  ],
  leiter: [
    {
      title: 'Team',
      topics: [
        { slug: 'benutzer-verwalten', title: 'Benutzer verwalten', icon: Users, ready: false },
        { slug: 'zeitmanagement', title: 'Abwesenheiten und Zeiten', icon: Clock, ready: false },
      ],
    },
    {
      title: 'Auswertung',
      topics: [{ slug: 'arbeitszeit-report', title: 'Arbeitszeit-Report', icon: BarChart3, ready: false }],
    },
  ],
}

export const findTopic = (slug: string | undefined): GuideTopic | undefined =>
  Object.values(GUIDE_PHASES)
    .flat()
    .flatMap((p) => p.topics).find((t) => t.slug === slug)
